'use client';

import { useApi } from '../api/Api';
import { RequestSnackbar, useRequest } from '../api/Request';
import { LoadingButton } from '@mui/lab';
import { FormControl, FormHelperText, Grid2, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { User } from '../database/user';

export enum SupportRequestType {
    GeneralIssue = 'General Issue',
    Improvement = 'Improvement',
    PaymentIssue = 'Payment Issue'
}

export interface SupportRequestProps {
    user?: User;
}

const SupportRequest: React.FC<SupportRequestProps> = ({ user }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState(user ? user.email : '');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [supportRequestType, setSupportRequestType] = useState('')
    const [errors, setErrors] = useState<Record<string, string>>({});

    const api = useApi();
    const request = useRequest<string>();
    const theme = useTheme();

    const onSubmit = () => {
        const newErrors: Record<string, string> = {};

        if (name.trim().length === 0) {
            newErrors.name = 'This field is required';
        }
        if (email.trim().length === 0) {
            newErrors.email = 'This field is required';
        }
        if (supportRequestType.trim().length === 0) {
            newErrors.supportRequestType = 'This field is required';
        }
        if (title.trim().length === 0) {
            newErrors.title = 'This field is required';
        }
        if (description.trim().length === 0) {
            newErrors.message = 'This field is required';
        }

        setErrors(newErrors);
        if (Object.values(newErrors).length > 0) {
            return;
        }

        request.onStart();
        api.createSupportRequest({
            name: name.trim(),
            email: email.trim(),
            requestType: supportRequestType.trim(),
            title: title.trim(),
            description: description.trim(),
        })
            .then(() => {
                request.onSuccess();
            })
            .catch((err) => {
                console.error(err);
                request.onFailure(err);
            });
    };

    const handleSupportRequestTypeChange = (event: SelectChangeEvent) => {
        setSupportRequestType(event.target.value as string);
    };

    if (request.data) {
        return (
            <Stack id='support-request'>
                <Typography variant='h4'>Contact Support</Typography>
                <Typography color='secondary' mb={2}>
                    Thank you for submitting the request! If necessary, we will get in touch with you.
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack id='support-request'>
            <Typography variant='h4'>Support Request</Typography>
            <Typography color='secondary' mb={2}>
                Experienced an issue? Thought of something cool you want to see?
                You've come to the right place.
            </Typography>
            <Grid2 container rowSpacing={2} columnSpacing={2}>
                <Grid2
                    size={{
                        xs: 12,
                        sm: 6,
                    }}
                >
                    <TextField
                        label='Full Name'
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        error={Boolean(errors.name)}
                        helperText={errors.name}
                        fullWidth
                    />
                </Grid2>

                <Grid2
                    size={{
                        xs: 12,
                        sm: 6,
                    }}
                >
                    <TextField
                        label='Email'
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        error={Boolean(errors.email)}
                        helperText={errors.email}
                        fullWidth
                    />
                </Grid2>

                <Grid2 size={12}>
                    <FormControl fullWidth error={Boolean(errors.supportRequestType)}>

                        <InputLabel id="support-request-type-select-label"
                            sx={{
                                color: theme.palette.secondary.main, // Default color
                                '&.Mui-focused': {
                                    color: theme.palette.secondary.main, // On focus (clicked)
                                },
                                '&.MuiFormLabel-filled': {
                                    color: theme.palette.secondary.main, // When value is selected
                                },
                            }}
                        >
                            Request Type
                        </InputLabel>
                        <Select
                            id="support-request-type-select"
                            value={supportRequestType}
                            label="Support Request Type"
                            onChange={handleSupportRequestTypeChange}
                            sx={{
                                color: theme.palette.secondary.main,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.secondary.main,
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.primary.main,
                                },
                                '& .MuiSelect-icon': {
                                    color: theme.palette.secondary.main,
                                },
                            }}
                        >
                            {
                                Object.values(SupportRequestType).map((opt, index) => (
                                    <MenuItem key={index} sx={{
                                        '&.Mui-selected': {
                                            color: theme.palette.secondary.main,
                                        },
                                    }}
                                        value={opt}>{opt}</MenuItem>
                                ))
                            }
                        </Select>
                        {errors.supportRequestType && (
                            <FormHelperText>
                                {errors.supportRequestType}
                            </FormHelperText>
                        )}
                    </FormControl>
                </Grid2>
                <Grid2 size={12}>
                    <TextField
                        label='Title'
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        error={Boolean(errors.message)}
                        helperText={errors.message}
                        fullWidth
                    />
                </Grid2>
                <Grid2 size={12}>
                    <TextField
                        label='Description'
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        error={Boolean(errors.message)}
                        helperText={errors.message}
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder='Please include as much detail as possible. Help us help you!'
                    />
                </Grid2>

                <Grid2 display='flex' justifyContent='center' size={12}>
                    <LoadingButton
                        variant='contained'
                        loading={request.isLoading()}
                        onClick={onSubmit}
                        fullWidth
                        sx={{
                            textTransform: 'none',
                            fontWeight: 'bold',
                            fontSize: '18px',
                            padding: '12px 16px',
                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                            mb: 2,
                        }}
                    >
                        Submit
                    </LoadingButton>
                </Grid2>
            </Grid2>
            <RequestSnackbar request={request} />
        </Stack>
    );
};

export default SupportRequest;
